import * as routes from 'routes';
import { AccountMode } from 'enums';
import { PendingChanges } from 'stores/account/model';
import {
  API_FUNCTION_TYPE,
  API_ACCOUNT_DETAIL_CREATE_DRAFT,
  API_ACCOUNT_DETAIL_WITHDRAWAL_CREATE,
  API_ACCOUNT_DETAIL_FEES_CREATE,
  API_ACCOUNT_DETAIL_WITHDRAWAL,
  API_ACCOUNT_DETAIL_CLIENT,
  API_ACCOUNT_DETAIL_TRANSFER
} from 'api/endpoints';

export enum DetailPage {
  DASHBOARD = 'dashboard',

  ID = 'id',
  PLAN = 'plan',
  FEES = 'fees',
  HISTORY = 'history',
  REPORTS = 'reports',
  MOVEMENTS = 'movements',
  SIGNED_DOCUMENTS = 'signedDocs',
  SIGNATURE = 'signature',
  PROFILE = 'profile',

  WITHDRAW = 'withdraw',
  WITHDRAW_ADJUST = 'adjust',

  SINGLE_FEES = 'singleFees',
  REGULAR_FEES = 'regularFees',

  PERSONAL_DATA = 'personalData',
  BANK_ACCOUNTS = 'bankAccounts',

  TRANSFER = 'transfer',
  TRANSFER_TARGET = 'transferTarget',

  OVERVIEW = 'overview',

  DOCS = 'docs',

  ADVISOR_INFO = 'advisor_info',
  DEPOSIT = 'deposit',
  CLIENT_VIEW = 'client_view',
}

export enum ConfirmType {
  EDIT = 'EDIT',
  CANCEL_EDIT = 'CANCEL_EDIT',
  CANCEL_SIGNATURE = 'CANCEL_SIGNATURE',
  CANCEL_PROCESSING = 'CANCEL_PROCESSING',
  CANCEL_REQUEST = 'CANCEL_REQUEST',
}

/* *********************************************************************** */


interface StepCondition {
  mode?: AccountMode;
}

export interface Flow {
  id: Flows;
  url: (accountId: string) => string;
  beFlow?: Flows;
  steps: { id: DetailPage; title: string; buttonTitle?: string; condition?: StepCondition }[];
  defaultPage?: DetailPage;
  menuItem?: DetailPage;
  pending: PendingChanges;
  endpoint: API_FUNCTION_TYPE;
  payload?: Object; // options for initiating the flow
  changeFlow?: boolean; // changes account settings, eg. plan, iq
}

export enum Flows {
  PLAN = 'PLAN',
  WITHDRAWAL = 'WITHDRAWAL',
  CLOSE_ACCOUNT = 'CLOSE_ACCOUNT',
  PRICELIST = 'PRICELIST',
  PERSONAL = 'PERSONAL',
  TRANSFER = 'TRANSFER'
}

export const flows: { [key in Flows]: Flow } = {
  [Flows.PLAN]: {
    id: Flows.PLAN,
    url: (accountId: string) => routes.ACCOUNTS_DETAIL_PLAN(accountId),
    pending: PendingChanges.PLAN,
    endpoint: API_ACCOUNT_DETAIL_CREATE_DRAFT,
    defaultPage: DetailPage.PLAN,
    menuItem: DetailPage.PLAN,
    changeFlow: true,
    steps: [
      { id: DetailPage.ID, title: 'accountDetail_investmentQuestionnaire' },
      { id: DetailPage.PLAN, title: 'accountDetail_investmentPlan', buttonTitle: 'accountStepper_planProposal' },
      { id: DetailPage.SINGLE_FEES, title: 'fees_single_title' },
      { id: DetailPage.REGULAR_FEES, title: 'fees_regular_title' },
      { id: DetailPage.OVERVIEW, title: 'accountDetail_overview' },
      { id: DetailPage.DOCS, title: 'accountDetail_documents' },
    ],
  },
  [Flows.WITHDRAWAL]: {
    id: Flows.WITHDRAWAL,
    url: (accountId: string) => routes.ACCOUNTS_DETAIL_WITHDRAWAL(accountId),
    pending: PendingChanges.WITHDRAWAL,
    endpoint: API_ACCOUNT_DETAIL_WITHDRAWAL_CREATE,
    menuItem: DetailPage.WITHDRAW,
    steps: [
      { id: DetailPage.WITHDRAW, title: 'accountDetail_withdraw' },
      { id: DetailPage.WITHDRAW_ADJUST, title: 'accountDetail_adjustReservesAndTargets' },
      { id: DetailPage.OVERVIEW, title: 'accountDetail_overview' },
      { id: DetailPage.DOCS, title: 'accountDetail_documents' },
    ],
  },
  [Flows.CLOSE_ACCOUNT]: {
    id: Flows.CLOSE_ACCOUNT,
    url: (accountId: string) => routes.ACCOUNTS_DETAIL_WITHDRAWAL(accountId),
    beFlow: Flows.WITHDRAWAL,
    pending: PendingChanges.WITHDRAWAL,
    endpoint: API_ACCOUNT_DETAIL_WITHDRAWAL,
    payload: { withdrawAll: true, closeAccount: true, standardFlow: false },
    menuItem: DetailPage.WITHDRAW,
    steps: [{ id: DetailPage.DOCS, title: 'accountDetail_documents' }],
  },
  [Flows.PRICELIST]: {
    id: Flows.PRICELIST,
    url: (accountId: string) => routes.ACCOUNTS_DETAIL_FEES(accountId),
    pending: PendingChanges.FEES,
    endpoint: API_ACCOUNT_DETAIL_FEES_CREATE,
    menuItem: DetailPage.SINGLE_FEES,
    changeFlow: true,
    steps: [
      { id: DetailPage.SINGLE_FEES, title: 'fees_single_title' },
      { id: DetailPage.REGULAR_FEES, title: 'fees_regular_title' },
      { id: DetailPage.DOCS, title: 'accountDetail_documents' },
    ],
  },
  [Flows.PERSONAL]: {
    id: Flows.PERSONAL,
    url: (accountId: string) => routes.ACCOUNTS_DETAIL_PERSONAL(accountId),
    pending: PendingChanges.PERSONAL,
    endpoint: API_ACCOUNT_DETAIL_CLIENT,
    menuItem: DetailPage.TRANSFER,
    changeFlow: true,
    steps: [
      { id: DetailPage.PERSONAL_DATA, title: 'accountDetail_personalData' },
      { id: DetailPage.BANK_ACCOUNTS, title: 'accountDetail_bankAccounts' },
      { id: DetailPage.DOCS, title: 'accountDetail_documents' },
    ],
  },
  [Flows.TRANSFER]: {
    id: Flows.TRANSFER,
    url: (accountId: string) => routes.ACCOUNTS_DETAIL_TRANSFER(accountId),
    pending: PendingChanges.TRANSFER,
    endpoint: API_ACCOUNT_DETAIL_TRANSFER,
    menuItem: DetailPage.TRANSFER,
    steps: [
      { id: DetailPage.TRANSFER, title: 'accountDetail_transfer' },
      { id: DetailPage.WITHDRAW_ADJUST, title: 'accountDetail_adjustReservesAndTargets' },
      { id: DetailPage.TRANSFER_TARGET, title: 'accountDetail_transferTarget' },
      { id: DetailPage.OVERVIEW, title: 'accountDetail_overview' },
      { id: DetailPage.DOCS, title: 'accountDetail_documents' },
    ],
  },
};

export const urls = [
  {
    id: DetailPage.DASHBOARD,
    title: 'accountDetail_dashboard',
    clientUrl: (vs: string) => routes.CLIENT_ACCOUNT_DETAIL_DASHBOARD(vs),
    advisorUrl: (accountId: string) => routes.ACCOUNTS_DETAIL(accountId),
  },
  {
    id: DetailPage.HISTORY,
    title: 'accountDetail_history',
    clientUrl: (vs: string) => routes.CLIENT_ACCOUNT_DETAIL_HISTORY(vs),
    advisorUrl: (accountId: string) => routes.ACCOUNTS_DETAIL_HISTORY(accountId),
  },
  {
    id: DetailPage.REPORTS,
    title: 'accountDetail_reportsAndSummaries',
    clientUrl: (vs: string) => routes.CLIENT_ACCOUNT_DETAIL_REPORTS(vs),
    advisorUrl: (accountId: string) => routes.ACCOUNTS_DETAIL_REPORTS(accountId),
  },
  {
    id: DetailPage.ID,
    title: 'accountDetail_investmentQuestionnaire',
    clientUrl: (vs: string) => routes.CLIENT_ACCOUNT_DETAIL_ID(vs),
    advisorUrl: (accountId: string) => routes.ACCOUNTS_DETAIL_ID(accountId),
    startFlow: Flows.PLAN,
    startFlowPage: DetailPage.ID
  },
  {
    id: DetailPage.PLAN,
    title: 'accountDetail_investmentPlan',
    clientUrl: (vs: string) => routes.CLIENT_ACCOUNT_DETAIL_PLAN(vs),
    advisorUrl: (accountId: string) => routes.ACCOUNTS_DETAIL_PLAN(accountId),
    startFlow: Flows.PLAN
  },
  {
    id: DetailPage.SIGNED_DOCUMENTS,
    title: 'accountDetail_signedDocuments',
    clientUrl: (vs: string) => routes.CLIENT_ACCOUNT_DETAIL_DOCUMENTS(vs),
    advisorUrl: (accountId: string) => routes.ACCOUNTS_DETAIL_DOCUMENTS(accountId),
  },
  {
    id: DetailPage.SIGNATURE,
    windowTitle: 'accountDetail_signature',
    title: undefined,
    clientUrl: (vs: string) => routes.CLIENT_ACCOUNT_DETAIL_SIGNATURE(vs),
    advisorUrl: (accountId: string) => '',
    signature: true,
  },
  {
    id: DetailPage.MOVEMENTS,
    title: 'accountDetail_movements',
    clientUrl: (vs: string) => '',
    advisorUrl: (accountId: string) => routes.ACCOUNTS_DETAIL_MOVEMENTS(accountId),
  },
  {
    id: DetailPage.FEES,
    title: 'accountDetail_fees',
    clientUrl: (vs: string) => '',
    advisorUrl: (accountId: string) => routes.ACCOUNTS_DETAIL_FEES(accountId),
    startFlow: Flows.PRICELIST
  },
  {
    id: DetailPage.PROFILE,
    title: 'accountDetail_personalData',
    clientUrl: (vs: string) => '',
    advisorUrl: (accountId: string) => routes.ACCOUNTS_DETAIL_PERSONAL(accountId),
    startFlow: Flows.PERSONAL
  },
];
